import React from 'react';
import { formatFilterPrice, Icon, IconEnum } from '@investoro/core';
import styled from 'styled-components';
import type { PlanBSectionDto } from 'utils/types/AiPredictorModels';

import { SectionCard, SectionHeader, SectionTitle, TextContent } from 'components/valuation/common';

const RentalGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const RentalTile = styled.div`
    background: var(--color-alongTermRental-second);
    border-radius: var(--box-border-radius);
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const RentalTileTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--font-size-body-1);
    font-weight: 600;
    color: var(--color-primary);
`;

const RentalMetric = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const RentalMetricLabel = styled.span`
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
    font-weight: 500;
`;

const RentalMetricValue = styled.span`
    font-size: 18px;
    font-weight: 700;
    color: var(--color-primary);
    line-height: 1.2;
`;

const RoiBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--background-color-ai);
    color: var(--color-alongTermRental);
    font-size: var(--font-size-body-2);
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
    align-self: flex-start;
`;

const RentalAiText = styled.p`
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
    font-style: italic;
    line-height: 1.5;
    margin: 0;
`;

interface ValuationPlanBProps {
    section: PlanBSectionDto;
}

const ValuationPlanB = ({ section }: ValuationPlanBProps) => {
    if (!section.longTermRental && !section.shortTermRental) return null;

    const { longTermRental, shortTermRental } = section;

    return (
        <SectionCard>
            <SectionHeader>
                <Icon icon={IconEnum.KEY} />
                <SectionTitle>Plan B – potencjał wynajmu</SectionTitle>
            </SectionHeader>
            {section.sectionDescription &&
                <TextContent>{section.sectionDescription}</TextContent>}
            <RentalGrid>
                {longTermRental &&
                    <RentalTile>
                        <RentalTileTitle>
                            <Icon icon={IconEnum.CALENDAR} />
                            Wynajem długoterminowy
                        </RentalTileTitle>
                        {longTermRental.rentPriceMin != null && longTermRental.rentPriceMax != null &&
                            <RentalMetric>
                                <RentalMetricLabel>Szacowany czynsz miesięczny</RentalMetricLabel>
                                <RentalMetricValue>
                                    {formatFilterPrice(longTermRental.rentPriceMin)} – {formatFilterPrice(longTermRental.rentPriceMax)} zł
                                </RentalMetricValue>
                            </RentalMetric>
                        }
                        {longTermRental.roi != null &&
                            <RoiBadge>ROI brutto: {longTermRental.roi}%</RoiBadge>
                        }
                        {longTermRental.aiDescription &&
                            <RentalAiText>{longTermRental.aiDescription}</RentalAiText>
                        }
                    </RentalTile>}
                {shortTermRental &&
                    <RentalTile>
                        <RentalTileTitle>
                            <Icon icon={IconEnum.STAR} />
                            Wynajem krótkoterminowy
                        </RentalTileTitle>
                        {shortTermRental.pricePerNightMin != null && shortTermRental.pricePerNightMax != null &&
                            <RentalMetric>
                                <RentalMetricLabel>Cena za dobę</RentalMetricLabel>
                                <RentalMetricValue>
                                    {formatFilterPrice(shortTermRental.pricePerNightMin)} – {formatFilterPrice(shortTermRental.pricePerNightMax)} zł
                                </RentalMetricValue>
                            </RentalMetric>}
                        {shortTermRental.estimatedOccupancy != null &&
                            <RoiBadge>Obłożenie: ~{shortTermRental.estimatedOccupancy}%</RoiBadge>}
                        {shortTermRental.monthlyProfitMin != null && shortTermRental.monthlyProfitMax != null &&
                            <RentalMetric>
                                <RentalMetricLabel>Szacowany zysk miesięczny</RentalMetricLabel>
                                <RentalMetricValue>
                                    {formatFilterPrice(shortTermRental.monthlyProfitMin)} – {formatFilterPrice(shortTermRental.monthlyProfitMax)} zł
                                </RentalMetricValue>
                            </RentalMetric>}
                        {shortTermRental.aiDescription &&
                            <RentalAiText>{shortTermRental.aiDescription}</RentalAiText>}
                    </RentalTile>
                }
            </RentalGrid>
        </SectionCard>
    );
};

export default ValuationPlanB;
